import { Link } from 'react-router-dom';
import { featuredCampaigns } from '@/mocks/homeData';

const urgencyColors: Record<string, string> = {
  critical: 'bg-primary-500',
  high: 'bg-primary-400',
  medium: 'bg-accent-500',
  low: 'bg-secondary-500',
};

const urgencyLabels: Record<string, string> = {
  critical: 'Critical',
  high: 'Urgent',
  medium: 'Active',
  low: 'Ongoing',
};

export default function FeaturedCampaigns() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <div>
            <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full mb-3">
              Featured Campaigns
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-3">
              Campaigns That Need{' '}
              <span className="gradient-text">Your Support</span>
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-lg">
              Every campaign is verified and monitored. Choose one that speaks to your heart.
            </p>
          </div>
          <Link
            to="/campaigns"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold text-sm whitespace-nowrap group"
          >
            View All Campaigns
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {featuredCampaigns.map((campaign, index) => {
            const progress = Math.round((campaign.raised / campaign.goal) * 100);
            const remaining = campaign.goal - campaign.raised;

            return (
              <div
                key={campaign.id}
                className="group bg-background-50 rounded-2xl overflow-hidden border border-foreground-200/50 hover:shadow-sm transition-all duration-300 flex flex-col"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10"></div>
                  <img
                    src={campaign.imageUrl}
                    alt={campaign.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 z-20">
                    <span className={`px-2.5 py-1 rounded-full text-white text-[10px] font-semibold ${urgencyColors[campaign.urgency]}`}>
                      {urgencyLabels[campaign.urgency]}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 z-20">
                    <button className="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors">
                      <i className="ri-share-forward-line text-foreground-700 text-sm"></i>
                    </button>
                  </div>
                </div>

                <div className="p-4 flex-1 flex flex-col">
                  <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-wide mb-1">{campaign.category}</span>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {campaign.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-3 mb-4">
                    {campaign.story}
                  </p>

                  <div className="mt-auto space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-foreground-700 font-semibold">₹{campaign.raised.toLocaleString()} raised</span>
                        <span className="text-foreground-400">{progress}%</span>
                      </div>
                      <div className="h-2 bg-foreground-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-1000"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-foreground-500">
                      <span>Goal: ₹{campaign.goal.toLocaleString()}</span>
                      <span>₹{remaining.toLocaleString()} to go</span>
                    </div>

                    <div className="flex items-center justify-between pt-1 border-t border-foreground-100">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-time-line text-foreground-400 text-xs"></i>
                        <span className="text-foreground-500 text-[11px]">{campaign.daysLeft} days left</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-user-line text-foreground-400 text-xs"></i>
                        <span className="text-foreground-500 text-[11px]">{campaign.donors.toLocaleString()} donors</span>
                      </div>
                    </div>

                    <Link
                      to={`/campaigns/${campaign.id}`}
                      className="block w-full text-center py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      Donate Now
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}